/**
 * Several files at once: picked together, uploaded a few at a time, each one
 * joining the cart the moment the server has its bytes.
 *
 * WHY NOT `useFileUpload`. That hook is deliberately a one-slot machine — its
 * state holds `file` singular — and a hook cannot be called once per row of a
 * list that grows. What is reused instead is its parts: `precheckFile`,
 * `apiUpload`, `isUploadCancelled`, and the idea behind its `runIdRef` guard,
 * lifted here to per-row scope because identity is now per file rather than
 * per hook.
 *
 * A ROW HERE IS THE ONLY CLIENT-SIDE CART STATE IN THE APP. It exists because
 * a file whose bytes are still moving has no upload id, so it cannot be a
 * cart row yet. The instant the server answers, the row is handed to
 * `POST /cart/items` and disappears from here — everything after that point is
 * server state. Reloading mid-transfer loses these, which is honest: the
 * bytes are gone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/lib/api/client';
import { addToCart } from '@/lib/api/cart';
import { apiUpload, isUploadCancelled } from '@/lib/api/uploads';
import type { CartOut, UploadLimits } from '@/lib/api/types';

import { precheckFile } from '../upload/precheck';

/**
 * Three at a time.
 *
 * Not driven by the rate limit — that counts uploads per hour, and running
 * them one at a time would use exactly as many. It is about the pipe: ten
 * parallel transfers on a phone connection give every file a progress bar
 * that barely moves, and make it likely they all hit the request timeout
 * together rather than finishing one by one.
 */
export const MAX_CONCURRENT_UPLOADS = 3;

export type QueuedStatus = 'queued' | 'uploading' | 'failed';

export interface QueuedUpload {
  /** Stable for the life of the row, assigned at pick time. The React key. */
  key: string;
  filename: string;
  status: QueuedStatus;
  /** 0-100 while bytes move; undefined when the length is not computable. */
  percent: number | undefined;
  /** Customer-facing prose. The server's own words whenever the server spoke. */
  error: string | null;
  /** True when pressing "try again" with the same file could work. */
  retryable: boolean;
}

export interface UseUploadQueueArgs {
  limits: UploadLimits | undefined;
  /** Spaces left in the cart, so a multi-select can be capped before it starts. */
  remainingSlots: number;
  /** Fired with the server's new cart each time a file lands in it. */
  onCartChanged: (cart: CartOut) => void;
  /**
   * Runs one cart-changing call at a time, shared with the screen's own edits.
   *
   * The queue has always serialised its OWN adds — see `handover` below — but
   * a chain of its own is not enough once the screen can change a file at the
   * same time. Both kinds of call answer with the whole cart, so an add and a
   * copies-change in flight together can return two snapshots of two
   * different moments, and whichever lands second wins. Handing both through
   * one queue is what makes "the last reply is the current cart" true.
   *
   * Optional: without it the queue falls back to its own chain, which is
   * correct for a caller that makes no other cart calls.
   */
  serialise?: <T>(work: () => Promise<T>) => Promise<T>;
}

export interface UploadQueue {
  rows: QueuedUpload[];
  /** Set while a 429 is in force. Nothing drains until it passes. */
  cooldownUntil: number | null;
  add: (files: File[]) => void;
  cancel: (key: string) => void;
  retryFailed: () => void;
  dismiss: (key: string) => void;
  /** How many files were dropped from the last pick for want of room. */
  overflow: number;
}

interface Pending {
  key: string;
  file: File;
}

let sequence = 0;
function nextKey(): string {
  sequence += 1;
  return `upload-${sequence}-${Date.now()}`;
}

export function useUploadQueue({
  limits,
  remainingSlots,
  onCartChanged,
  serialise,
}: UseUploadQueueArgs): UploadQueue {
  const [rows, setRows] = useState<QueuedUpload[]>([]);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [overflow, setOverflow] = useState(0);

  const pending = useRef<Pending[]>([]);
  const inFlight = useRef(new Map<string, AbortController>());
  /** Rows the customer cancelled or dismissed. A late success for one of these
   *  must not resurrect it, and must not push the file into the cart. */
  const abandoned = useRef(new Set<string>());
  const files = useRef(new Map<string, File>());
  /** Read inside async callbacks, which would otherwise close over a stale value. */
  const cooldownRef = useRef<number | null>(null);
  /**
   * Adds to the cart run one after another, however many uploads finish at
   * once.
   *
   * Every add answers with the WHOLE cart, so three replies are three
   * snapshots taken at three different moments — and applying them in
   * arrival order means the earliest snapshot can land last and wipe the
   * two files that followed it. Uploading in parallel is worth it; adding in
   * parallel is not, because the server takes a row lock per cart and
   * serialises them anyway.
   */
  const handover = useRef<Promise<void>>(Promise.resolve());
  const mounted = useRef(true);
  /**
   * Whether an upload has succeeded yet, which is the only way to know the
   * browser is holding an anonymous session cookie.
   *
   * IT MATTERS BECAUSE THE COOKIE IS MINTED BY THE UPLOAD ITSELF. A visitor
   * with no session who picks three files fires three uploads at once, each
   * of which mints a session of its own; the browser keeps the last, and the
   * first two files now belong to sessions it can no longer present. They
   * upload fine and are then refused by the cart with "we could not find
   * that upload" — on the very first multi-select of every new visitor.
   *
   * So the first transfer goes alone and establishes the session, and the
   * rest fan out behind it. The cost is one serial upload per page load.
   */
  const primed = useRef(false);

  // Held in refs because the drain loop is async and must see the CURRENT
  // callback, not the one that existed when a transfer started.
  const onCartChangedRef = useRef(onCartChanged);
  onCartChangedRef.current = onCartChanged;
  const serialiseRef = useRef(serialise);
  serialiseRef.current = serialise;

  /** The caller's queue when there is one, otherwise the local chain above. */
  function queued<T>(work: () => Promise<T>): Promise<T> {
    const shared = serialiseRef.current;
    if (shared) return shared(work);
    const next = handover.current.then(work, work);
    handover.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  useEffect(() => {
    mounted.current = true;
    // Captured rather than read in the cleanup: the Map is created once and
    // never replaced, so this is the same object either way — but the lint
    // rule cannot know that, and the rule is right in general.
    const transfers = inFlight.current;
    return () => {
      mounted.current = false;
      for (const controller of transfers.values()) controller.abort();
      transfers.clear();
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<QueuedUpload>) => {
    if (!mounted.current) return;
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));
  }, []);

  const drop = useCallback((key: string) => {
    files.current.delete(key);
    if (!mounted.current) return;
    setRows((current) => current.filter((row) => row.key !== key));
  }, []);

  const drain = useCallback(() => {
    if (!mounted.current) return;
    if (cooldownRef.current && Date.now() < cooldownRef.current) return;

    const limit = primed.current ? MAX_CONCURRENT_UPLOADS : 1;
    while (inFlight.current.size < limit && pending.current.length > 0) {
      const next = pending.current.shift();
      if (!next || abandoned.current.has(next.key)) continue;

      const controller = new AbortController();
      inFlight.current.set(next.key, controller);
      patch(next.key, { status: 'uploading', percent: 0, error: null });

      void apiUpload({
        file: next.file,
        signal: controller.signal,
        onProgress: (progress) => patch(next.key, { percent: progress.percent }),
      })
        .then((result) => {
          // Cancelled while the bytes were in the air. The server may well
          // have the file; it will expire unreferenced, which is cheaper than
          // a row the customer already dismissed coming back.
          // The session cookie now exists, so the rest can go in parallel.
          primed.current = true;
          if (abandoned.current.has(next.key)) return undefined;

          return queued(async () => {
            if (abandoned.current.has(next.key)) return;
            try {
              const cart = await addToCart(result.upload_id);
              if (abandoned.current.has(next.key)) return;
              onCartChangedRef.current(cart);
              drop(next.key);
            } catch (cause) {
              // Caught here, not allowed out: one file failing to join the
              // cart must not break the chain for the files behind it.
              const error = cause instanceof ApiError ? cause : null;
              patch(next.key, {
                status: 'failed',
                percent: undefined,
                error: error?.message ?? 'That file could not be added to your order.',
                retryable: false,
              });
            }
          });
        })
        .catch((cause: unknown) => {
          if (isUploadCancelled(cause) || abandoned.current.has(next.key)) {
            drop(next.key);
            return;
          }
          const error = cause instanceof ApiError ? cause : null;

          // A 429 stops the WHOLE queue, not just this file. Carrying on
          // means every remaining file 429s too and burns the per-IP budget
          // on requests that cannot succeed.
          if (error?.status === 429) {
            const waitMs = Math.max(1, error.retryAfterSeconds ?? 60) * 1000;
            cooldownRef.current = Date.now() + waitMs;
            if (mounted.current) setCooldownUntil(cooldownRef.current);
          }

          patch(next.key, {
            status: 'failed',
            percent: undefined,
            error: error?.message ?? 'That file did not upload. Please try again.',
            retryable: error ? error.status === 429 || error.isRetryable : true,
          });
        })
        .finally(() => {
          inFlight.current.delete(next.key);
          drain();
        });
    }
  }, [drop, patch]);

  // Resume on its own once the server's wait has elapsed, but only to RESTART
  // the drain — files that already failed stay failed until the customer asks
  // again, because an automatic retry that 429s a second time reads as a
  // broken app rather than a busy one.
  useEffect(() => {
    if (cooldownUntil === null) return;
    const delay = Math.max(0, cooldownUntil - Date.now());
    const timer = setTimeout(() => {
      cooldownRef.current = null;
      setCooldownUntil(null);
      drain();
    }, delay);
    return () => clearTimeout(timer);
  }, [cooldownUntil, drain]);

  const add = useCallback(
    (picked: File[]) => {
      // Capped BEFORE anything is uploaded. Letting eleven files through and
      // failing the last one would waste the customer's data and a unit of
      // their hourly allowance on a file that was never going to fit.
      const room = Math.max(0, remainingSlots - pending.current.length - inFlight.current.size);
      const accepted = picked.slice(0, room);
      setOverflow(picked.length - accepted.length);

      const fresh: QueuedUpload[] = [];
      for (const file of accepted) {
        const key = nextKey();
        const check = precheckFile(file, limits);
        if (!check.ok) {
          // Rejected here, so it never consumes a slot or a unit of the
          // hourly allowance. Not retryable: the same file fails the same way.
          fresh.push({
            key,
            filename: file.name,
            status: 'failed',
            percent: undefined,
            error: check.message,
            retryable: false,
          });
          continue;
        }
        files.current.set(key, file);
        pending.current.push({ key, file });
        fresh.push({
          key,
          filename: file.name,
          status: 'queued',
          percent: undefined,
          error: null,
          retryable: false,
        });
      }

      setRows((current) => [...current, ...fresh]);
      drain();
    },
    [drain, limits, remainingSlots],
  );

  const cancel = useCallback(
    (key: string) => {
      abandoned.current.add(key);
      inFlight.current.get(key)?.abort();
      pending.current = pending.current.filter((entry) => entry.key !== key);
      drop(key);
      drain();
    },
    [drain, drop],
  );

  const dismiss = cancel;

  const retryFailed = useCallback(() => {
    const retryable = rows.filter((row) => row.status === 'failed' && row.retryable);
    for (const row of retryable) {
      const file = files.current.get(row.key);
      if (!file) continue;
      pending.current.push({ key: row.key, file });
      patch(row.key, { status: 'queued', error: null, percent: undefined });
    }
    drain();
  }, [drain, patch, rows]);

  return { rows, cooldownUntil, add, cancel, retryFailed, dismiss, overflow };
}
